import {
  AfterContentInit,
  ComponentRef,
  createComponent,
  Directive,
  DoCheck,
  effect,
  EnvironmentInjector,
  inject,
  input,
  OnDestroy,
} from '@angular/core';
import { Table } from '@openng/optimus-ui/table';
import { DEFAULT_PAGE_OPTIONS } from '@shared/utils';
import { BrowseTableTemplatesComponent } from './browse-table-templates.component';

/**
 * Tabulka přehledu: `<p-table uiBrowseTable>` dostane společné nastavení (lazy load bez načtení při startu,
 * stránkování s výběrem počtu řádků a hláškou „Zobrazeno …“, posuvná tabulka, min. šířka) a výchozí šablony
 * ikony řazení a prázdné tabulky. Vlastní `#sorticon` / `#emptymessage` v obsahu tabulky mají přednost.
 *
 * Data zůstávají v šabloně přehledu: `[value]`, `[totalRecords]`, `[rows]`, `[first]`, `[sortField]`,
 * `[sortOrder]` a `(onLazyLoad)` – stejně jako sloupce v `#header` / `#body`.
 *
 * Vstupy `lazy`, `lazyLoadOnInit`, `paginator`, `rows` a `scrollHeight` se jmenují stejně jako vstupy Table:
 * binding na p-table nastaví oba, takže se direktiva a tabulka nerozejdou. Do Table je direktiva zapisuje
 * v konstruktoru (výchozí hodnoty platí už pro ngOnInit tabulky) a v ngDoCheck, tj. po bindingech p-table
 * a před překreslením tabulky.
 */
@Directive({
  selector: 'p-table[uiBrowseTable]',
  host: { class: 'p-datatable-lg' },
})
export class BrowseTableDirective implements DoCheck, AfterContentInit, OnDestroy {
  private readonly table = inject(Table);
  private readonly environmentInjector = inject(EnvironmentInjector);

  /** Stránkování na BE přes `(onLazyLoad)`; false = tabulka stránkuje načtená data sama. */
  readonly lazy = input(true);
  /** Lazy load hned při vzniku tabulky; přehledy načítají samy (filtr). */
  readonly lazyLoadOnInit = input(false);
  /** `auto` = stránkování jen při víc záznamech (`totalRecords`), než je `[rows]` ze šablony. */
  readonly paginator = input<boolean | 'auto'>(true);
  /** Počet řádků ze šablony; direktiva ho čte jen pro `paginator="auto"` (Table si rows mění při stránkování). */
  readonly rows = input<number>();
  /** Výška posuvné části tabulky; '' = bez omezení (tabulka roste s obsahem). */
  readonly scrollHeight = input('600px');
  /** Hláška prázdné tabulky; null = tělo prázdné tabulky zůstane prázdné. */
  readonly emptyText = input<string | null>('Nejsou data k zobrazení.');

  private templates: ComponentRef<BrowseTableTemplatesComponent> | null = null;

  constructor() {
    const table = this.table;
    table.scrollable = true;
    table.showCurrentPageReport = true;
    table.rowsPerPageOptions = DEFAULT_PAGE_OPTIONS;
    table.currentPageReportTemplate = 'Zobrazeno {first}-{last} z {totalRecords} záznamů';
    table.paginatorDropdownAppendTo = 'body';
    table.tableStyle = { 'min-width': '50rem' };
    this.syncTable();

    effect(() => {
      this.templates?.setInput('emptyText', this.emptyText());
    });
  }

  ngDoCheck(): void {
    this.syncTable();
  }

  ngAfterContentInit(): void {
    // Table už má zpracované vlastní content šablony; doplníme jen ty, které přehled nedodal.
    const table = this.table as Table & { _sortIconTemplate?: unknown; _emptyMessageTemplate?: unknown };
    const needsSortIcon = !table.sortIconTemplate && !table._sortIconTemplate;
    const needsEmptyMessage = !table.emptyMessageTemplate && !table._emptyMessageTemplate;
    if (!needsSortIcon && !needsEmptyMessage) return;

    this.templates = createComponent(BrowseTableTemplatesComponent, { environmentInjector: this.environmentInjector });
    this.templates.setInput('emptyText', this.emptyText());
    this.templates.changeDetectorRef.detectChanges();

    if (needsSortIcon) table.sortIconTemplate = this.templates.instance.sortIcon();
    if (needsEmptyMessage) table.emptyMessageTemplate = this.templates.instance.emptyMessage();
  }

  ngOnDestroy(): void {
    this.templates?.destroy();
  }

  private syncTable(): void {
    const table = this.table;
    table.lazy = this.lazy();
    table.lazyLoadOnInit = this.lazyLoadOnInit();
    table.scrollHeight = this.scrollHeight();
    const paginator = this.paginator();
    table.paginator = paginator === 'auto' ? (table.totalRecords || 0) > (this.rows() ?? 0) : paginator;
  }
}
